/**
 * Modal d'aide expliquant le système LMD aux étudiants.
 */
import React from 'react';

interface Props {
  onClose: () => void;
}

export const AideLMD: React.FC<Props> = ({ onClose }) => {
  return (
    <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4">
      <div className="bg-gray-900 rounded-t-lg sm:rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        <div className="px-5 py-4 border-b border-gray-800 flex items-center justify-between sticky top-0 bg-gray-900">
          <h3 className="font-semibold text-gray-100">Comprendre mon relevé</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-300 text-2xl leading-none">×</button>
        </div>

        <div className="p-5 space-y-5 text-sm text-gray-200">
          <section>
            <h4 className="font-semibold text-gray-100 mb-2">Calcul des moyennes</h4>
            <div className="pl-3 border-l-2 border-blue-300 space-y-3">

              {/* Étape 1 — ECUE */}
              <div>
                <p className="font-semibold text-xs text-blue-300">1) Moyenne d'une matière (ECUE)</p>
                <p className="font-mono text-xs bg-gray-800/50 p-2 rounded border border-gray-800 mt-1">
                  Moy. ECUE = (Moy. Devoirs × 40%) + (Examen × 60%)
                </p>
                <p className="text-[11px] text-gray-400 mt-1">
                  avec Moy. Devoirs = (D1 + D2) / 2.
                </p>
              </div>

              {/* Étape 2 — UE */}
              <div>
                <p className="font-semibold text-xs text-blue-300">2) Moyenne d'une UE</p>
                <p className="font-mono text-xs bg-gray-800/50 p-2 rounded border border-gray-800 mt-1">
                  Moy. UE = moyenne arithmétique des Moy. ECUE
                </p>
                <p className="text-[11px] text-gray-400 mt-1">
                  Toutes les matières d'une UE ont le même poids dans la moyenne de l'UE.
                </p>
              </div>

              {/* Étape 3 — Semestre : pondérée par crédits ECTS */}
              <div>
                <p className="font-semibold text-xs text-blue-300">
                  3) Moyenne du semestre — pondérée par crédits ECTS
                </p>
                <p className="font-mono text-xs bg-gray-800/50 p-2 rounded border border-gray-800 mt-1 whitespace-pre-wrap">
                  Moy. Sem = Σ ( Moy.UE × créditsECTS )
                  &#10;          ─────────────────────────
                  &#10;                Σ créditsECTS
                </p>
                <p className="text-[11px] text-gray-400 mt-1">
                  Chaque UE pèse selon ses crédits ECTS dans la moyenne du semestre.
                  Une UE à 6 crédits compte donc 2× plus qu'une UE à 3 crédits.
                </p>

                {/* Exemple concret pour rendre la formule tangible */}
                <div className="mt-2 bg-blue-900/20 border border-blue-800/50 rounded p-2 text-[11px]">
                  <div className="font-semibold text-blue-300 mb-1">Exemple :</div>
                  <div className="text-gray-300 space-y-0.5">
                    <div>UE1 : 12/20 × 6 crédits = 72</div>
                    <div>UE2 : 14/20 × 4 crédits = 56</div>
                    <div>UE3 : 10/20 × 5 crédits = 50</div>
                    <div className="pt-1 border-t border-blue-800/30 mt-1">
                      Total points = 178 — Total crédits = 15
                    </div>
                    <div className="font-bold text-blue-200">
                      Moy. Sem = 178 / 15 = <span className="text-base">11,87/20</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </section>

          <section>
            <h4 className="font-semibold text-gray-100 mb-2">Mentions LMD</h4>
            <table className="w-full text-xs border border-gray-800">
              <tbody>
                <tr className="border-b border-gray-800">
                  <td className="px-3 py-1.5 font-semibold text-gray-100">Très Bien</td>
                  <td className="px-3 py-1.5 text-gray-300">≥ 16/20</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="px-3 py-1.5 font-semibold text-gray-100">Bien</td>
                  <td className="px-3 py-1.5 text-gray-300">≥ 14/20</td>
                </tr>
                <tr className="border-b border-gray-800">
                  <td className="px-3 py-1.5 font-semibold text-gray-100">Assez Bien</td>
                  <td className="px-3 py-1.5 text-gray-300">≥ 12/20</td>
                </tr>
                <tr>
                  <td className="px-3 py-1.5 font-semibold text-gray-100">Passable</td>
                  <td className="px-3 py-1.5 text-gray-300">≥ 10/20</td>
                </tr>
              </tbody>
            </table>
          </section>

          <section>
            <h4 className="font-semibold text-gray-100 mb-2">Validation des UE</h4>
            <ul className="space-y-1.5 list-disc list-inside text-xs">
              <li>UE <strong>validée directement</strong> si sa moyenne ≥ 10/20</li>
              <li>UE <strong>validée par compensation</strong> si 8 ≤ moyenne &lt; 10 ET le semestre est validé : <span className="text-amber-300">vous obtenez la <strong>totalité des crédits ECTS</strong></span> de cette UE comme si vous l'aviez validée directement.</li>
              <li>UE <strong>éliminatoire</strong> si moyenne &lt; 6 → bloque le semestre, rattrapage obligatoire.</li>
              <li>Sur votre relevé, une étiquette indique comment l'UE a été validée : <em>session ordinaire</em>, <em>rattrapage</em> ou <em>compensation</em>.</li>
            </ul>
          </section>

          <section>
            <h4 className="font-semibold text-gray-100 mb-2">Rattrapage</h4>
            <p className="text-xs">
              Le rattrapage concerne les matières avec moyenne ECUE &lt; 10.
              Une fois la session de rattrapage close, votre relevé est mis à jour
              avec les notes définitives.
            </p>
          </section>

          <section>
            <h4 className="font-semibold text-gray-100 mb-2">Validation du semestre</h4>
            <ul className="space-y-1 list-disc list-inside text-xs">
              <li>Moyenne semestrielle ≥ 10/20</li>
              <li>Aucune UE éliminatoire en échec</li>
              <li>30 crédits ECTS obtenus</li>
            </ul>
          </section>

          <section className="bg-orange-900/30 border border-orange-800 rounded p-3">
            <h4 className="font-semibold text-orange-300 mb-1 text-sm">Relevé provisoire</h4>
            <p className="text-xs text-orange-300">
              Susceptible d'être modifié après délibération. Vous pouvez déposer une réclamation depuis votre relevé.
            </p>
          </section>
        </div>

        <div className="px-5 py-3 border-t border-gray-800 sticky bottom-0 bg-gray-900">
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-medium rounded-lg"
          >
            J'ai compris
          </button>
        </div>
      </div>
    </div>
  );
};
